/**
 * Saneamento de nome de arquivo para download.
 *
 * O Windows recusa `< > : " / \ | ? *` em nome de arquivo. Quando o nome gerado
 * contém um deles — e o nome do evento contém, como em "PETROBRAS :: FAVELA
 * SOUNDS" — o comportamento depende do navegador: alguns substituem, outros
 * truncam no caractere inválido. Truncar costuma levar junto a extensão, e sem
 * `.xlsx` o Windows não associa o arquivo ao Excel: o usuário relata que "não
 * abre", quando na verdade o conteúdo está íntegro.
 *
 * Isso explica o problema atingir uns usuários e outros não, com o mesmo
 * arquivo: a diferença está no navegador, não na planilha.
 */

const INVALIDOS_WINDOWS = /[<>:"/\\|?*]/g;

/** Nomes reservados pelo Windows, que não podem ser usados nem com extensão. */
const RESERVADOS =
  /^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])$/i;

/**
 * Devolve um nome de arquivo seguro em Windows, macOS e Linux.
 * Não inclui a extensão — acrescente depois de chamar.
 */
export function sanitizarNomeArquivo(nome: string, maxLength = 120): string {
  let seguro = (nome || "")
    .replace(INVALIDOS_WINDOWS, "_")
    // Caracteres de controle quebram tanto o nome quanto o cabeçalho de download.
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    // Windows descarta ponto e espaço no fim do nome.
    .replace(/[. ]+$/, "");

  if (seguro.length > maxLength) {
    seguro = seguro.substring(0, maxLength).replace(/[. ]+$/, "");
  }

  if (!seguro || RESERVADOS.test(seguro)) seguro = `arquivo_${seguro}`.trim();

  return seguro;
}

/**
 * Dispara o download de um blob com nome já saneado.
 * Concentra a criação e a liberação da object URL num lugar só.
 */
export function baixarArquivo(blob: Blob, nomeBase: string, extensao: string) {
  const nome = `${sanitizarNomeArquivo(nomeBase)}.${extensao.replace(/^\./, "")}`;
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = nome;
  document.body.appendChild(link);
  link.click();
  link.remove();

  // Liberar só depois do clique ser processado: revogar cedo demais cancela o
  // download em alguns navegadores.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
